import React, { useEffect, useRef, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Skeleton, Spin, Tabs, Card, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { Form } from 'antd';
import {
	getIcdCodesApi,
	getIcdCodesReset,
} from '../../actions/getIcdCodesAction';
import {
	getCptCodesApi,
	getCptCodesReset,
} from '../../actions/getCptCodesAction';
import { XTheme2Form, XTheme2Select } from '../../styles/GlobalThemeStyleCQ';
import { XTabIcdCpt, XThemeCardCaseOperations } from '../../pages/CaseForm/CaseFormStyle';
import { XTitleHead, XButtonConfirm, XViewMoreBTN } from '../../pages/AnalyseCoverage/AnalyseCoverageStyle';

const CardCodingTools = (props) => {
	const {
		// Props from parent component
		handleCodeClick,
		aipa = false,
		previewMode = false,
		
		// Redux props
		getIcdCodesState,
		getCptCodesState,
		getIcdCodes,
		getIcdCodesReset,
		getCptCodes,
		getCptCodesReset,
	} = props;

	// Form instances
	const [icdform] = Form.useForm();
	const [cptform] = Form.useForm();

	// State variables
	const [activeTab, setActiveTab] = useState('1');
	const [icdCodes, setIcdCodes] = useState({});
	const [cptCodes, setCptCodes] = useState({});
	const [searchResults, setSearchResults] = useState([]);
	const [cptSearchResults, setCptSearchResults] = useState([]);
	const [icdLoading, setIcdLoading] = useState(false);
	const [cptLoading, setCptLoading] = useState(false);
	const [icdSearchLoading, setIcdSearchLoading] = useState(false);
	const [cptSearchLoading, setCptSearchLoading] = useState(false);
	const [triggeredBySearch, setTriggeredBySearch] = useState(false);
	const [triggeredByEnter, setTriggeredByEnter] = useState(false);
	const [cptTriggeredBySearch, setCptTriggeredBySearch] = useState(false);
	const [cptTriggeredByEnter, setCptTriggeredByEnter] = useState(false);
	const [loadMore, setLoadMore] = useState(false);
	const [cptLoadMore, setCptLoadMore] = useState(false);
	const [icdFilters, setIcdFilters] = useState({});
	const [cptFilters, setCptFilters] = useState({});

	// Refs for debouncing
	const searchTimeoutRef = useRef(null);
	const latestSearchRef = useRef('');

	// Handle filters update
	const handleIcdFilters = (search, type) => {
		if (type === 'CPT') {
			setCptFilters((prev) => ({
				search: search,
			}));
		} else {
			setIcdFilters((prev) => ({
				search: search,
			}));
		}
	};

	// Handle Enter key press
	const handleSearchKeyDown = (e, type) => {
		if (type === 'CPT' && e.key === 'Enter' && e.target.value) {
			// Cancel any pending debounced searches
			debouncedSearch.cancel();
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
			// Store the latest search term
			latestSearchRef.current = e.target.value;
			setCptSearchLoading(false);
			setCptLoading(true);
			handleIcdFilters(e.target.value, 'CPT');
			cptform.setFieldsValue({ cpt_search: e.target.value });
			setCptTriggeredBySearch(false);
			setCptTriggeredByEnter(true);
			fetchCptCodes({ search: e.target.value });
		} else if (e.key === 'Enter' && e.target.value) {
			// Cancel any pending debounced searches
			debouncedSearch.cancel();
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
			// Store the latest search term
			latestSearchRef.current = e.target.value;
			setIcdSearchLoading(false);
			setIcdLoading(true);
			handleIcdFilters(e.target.value);
			icdform.setFieldsValue({ icd_search: e.target.value });
			setTriggeredBySearch(false);
			setTriggeredByEnter(true);
			fetchIcdCodes({ search: e.target.value });
		}
	};

	// Create a debounced search function
	const debouncedSearch = useCallback(
		debounce((searchTerm, type) => {
			// Only proceed if this is still the latest search term
			if (searchTerm === latestSearchRef.current) {
				if (type === 'CPT') {
					setCptSearchLoading(true);
					cptform.setFieldsValue({ cpt_search: searchTerm });
					handleIcdFilters(searchTerm, 'CPT');
					setCptTriggeredBySearch(true);
					setCptTriggeredByEnter(false);
					fetchCptCodes({ search: searchTerm });
				} else {
					setIcdSearchLoading(true);
					icdform.setFieldsValue({ icd_search: searchTerm });
					handleIcdFilters(searchTerm);
					setTriggeredBySearch(true);
					setTriggeredByEnter(false);
					fetchIcdCodes({ search: searchTerm });
				}
			}
		}, 300),
		[],
	);

	// Handle search change
	const handleSearchChange = (search, type) => {
		if (!search) return;

		if (search.length >= 3) {
			// Clear any existing timeout
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}

			// Store the latest search term
			latestSearchRef.current = search;

			// Cancel previous debounced call
			debouncedSearch.cancel();

			// Trigger the debounced search
			debouncedSearch(search, type);
		}
	};

	// Fetch ICD codes
	const fetchIcdCodes = (filters) => {
		getIcdCodes(filters);
	};

	// Fetch CPT codes
	const fetchCptCodes = (filters) => {
		getCptCodes(filters);
	};

	// Handle loading more records
	const handleLoadMore = (loaded_count, type) => {
		if (type === 'CPT') {
			setCptFilters((prev) => ({
				...prev,
				loaded_count: loaded_count,
			}));
			setCptLoadMore(true);
		} else {
			setIcdFilters((prev) => ({
				...prev,
				loaded_count: loaded_count,
			}));
			setLoadMore(true);
		}
	};

	// Handle code click
	const handleCodingToolsCodeClick = (type, codeData) => {
		if (handleCodeClick) {
			handleCodeClick(type, codeData);
		}
	};

	// Cleanup on component unmount
	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
			debouncedSearch.cancel();
		};
	}, []);

	// Handle ICD codes state changes
	useEffect(() => {
		if (getIcdCodesState.apiState === 'loading') {
			if (!icdSearchLoading) {
				setIcdLoading(true);
			}
		}
		if (getIcdCodesState.apiState === 'success') {
			if (triggeredBySearch && !triggeredByEnter) {
				// If just typing, populate search results only
				setSearchResults(getIcdCodesState.data.list);
			} else if (loadMore && !triggeredBySearch && !triggeredByEnter) {
				// Spread the old data and add new data when loading more
				setIcdCodes((prevCodes) => ({
					...prevCodes,
					list: [...prevCodes.list, ...getIcdCodesState.data.list],
					loaded_count:
						prevCodes.loaded_count + getIcdCodesState.data.loaded_count,
					total_count: getIcdCodesState.data.total_count,
				}));
				setSearchResults();
			} else if (triggeredByEnter && !triggeredBySearch && !loadMore) {
				// If Enter was pressed (full search), populate ICD codes below
				setIcdCodes(getIcdCodesState.data);
				setSearchResults();
			}
			setIcdLoading(false);
			setTriggeredBySearch(false);
			setIcdSearchLoading(false);
			setLoadMore(false);
			setTriggeredByEnter(false);
		}
		if (getIcdCodesState.apiState === 'error') {
			setIcdLoading(false);
			setTriggeredBySearch(false);
			setIcdSearchLoading(false);
			setLoadMore(false);
			setTriggeredByEnter(false);
		}
		getIcdCodesReset();
	}, [getIcdCodesState]);

	// Handle CPT codes state changes
	useEffect(() => {
		if (getCptCodesState.apiState === 'loading') {
			if (!cptSearchLoading) {
				setCptLoading(true);
			}
		}
		if (getCptCodesState.apiState === 'success') {
			if (cptTriggeredBySearch && !cptTriggeredByEnter) {
				// If just typing, populate search results only
				setCptSearchResults(getCptCodesState.data.list);
			} else if (cptLoadMore && !cptTriggeredBySearch && !cptTriggeredByEnter) {
				// Spread the old data and add new data when loading more
				setCptCodes((prevCodes) => ({
					...prevCodes,
					list: [...prevCodes.list, ...getCptCodesState.data.list],
					loaded_count:
						prevCodes.loaded_count + getCptCodesState.data.loaded_count,
					total_count: getCptCodesState.data.total_count,
				}));
				setCptSearchResults();
			} else if (cptTriggeredByEnter && !cptTriggeredBySearch && !cptLoadMore) {
				// If Enter was pressed (full search), populate CPT codes below
				setCptCodes(getCptCodesState.data);
				setCptSearchResults();
			}
			setCptLoading(false);
			setCptTriggeredBySearch(false);
			setCptSearchLoading(false);
			setCptLoadMore(false);
			setCptTriggeredByEnter(false);
		}
		if (getCptCodesState.apiState === 'error') {
			setCptLoading(false);
			setCptTriggeredBySearch(false);
			setCptSearchLoading(false);
			setCptLoadMore(false);
			setCptTriggeredByEnter(false);
		}
		getCptCodesReset();
	}, [getCptCodesState]);

	return (
		<XThemeCardCaseOperations
			className="haveHeader bgColorCodingTools"
			title={
				<Row gutter={[15, 15]} className="align-items-center">
					<Col xs={20} lg={12}>
						<Space align="center">
							<img
								className="iconResp"
								src="/images/coding_tool.svg"
								alt=""
							/>
							<XTitleHead>
								<span className="color2">Coding Tools</span>
							</XTitleHead>
						</Space>
					</Col>
					<Col xs={12} lg={12} className="text-end">
						<XTabIcdCpt
							type="card"
							activeKey={activeTab}
							onChange={setActiveTab}
							size="small"
							items={[
								{
									key: '1',
									label: 'ICD-10',
									disabled: aipa,
								},
								{
									key: '2',
									label: 'CPT',
								},
							]}
						/>
					</Col>
				</Row>
			}
		>
			<div className="p-3">
				{/* Search Bar */}
				<Row className="mb-3">
					<Col xs={24}>
						{activeTab === '1' ? (
							<XTheme2Form form={icdform} name="icdform">
								<XTheme2Form.Item name="icd_search">
									<XTheme2Select
										className="noBorderRadius"
										allowClear
										defaultActiveFirstOption={false}
										style={{ width: '100%' }}
										suffixIcon={null}
										showSearch
										notFoundContent={
											icdSearchLoading ? <Spin size="small" /> : null
										}
										onSearch={(searchTerm) =>
											handleSearchChange(searchTerm, 'ICD')
										}
										onClear={() => {
											icdform.resetFields();
											setSearchResults([]);
											setIcdCodes({});
										}}
										onInputKeyDown={(e) =>
											handleSearchKeyDown(e, 'ICD')
										}
										filterOption={false}
										options={
											searchResults && searchResults.length > 0
												? searchResults.map((item) => ({
														label: `${item.icd_code}: ${item.icd_title}`,
														value: item.icd_code,
													}))
												: []
										}
										onSelect={(value, option) => {
											handleCodingToolsCodeClick('ICD-10', {
												code: option.value,
												description: option.label,
												codeTypeText: 'pastHistory',
												buttontext: 'Add in Past',
											});
										}}
										size="large"
										placeholder="Search ICD-10 Code"
									/>
								</XTheme2Form.Item>
							</XTheme2Form>
						) : (
							<XTheme2Form form={cptform} name="cptform">
								<XTheme2Form.Item name="cpt_search">
									<XTheme2Select
										className="noBorderRadius"
										allowClear
										defaultActiveFirstOption={false}
										style={{ width: '100%' }}
										suffixIcon={null}
										showSearch
										notFoundContent={
											cptSearchLoading ? <Spin size="small" /> : null
										}
										onSearch={(searchTerm) =>
											handleSearchChange(searchTerm, 'CPT')
										}
										onClear={() => {
											cptform.resetFields();
											setCptSearchResults([]);
											setCptCodes({});
										}}
										onInputKeyDown={(e) =>
											handleSearchKeyDown(e, 'CPT')
										}
										filterOption={false}
										options={
											cptSearchResults && cptSearchResults.length > 0
												? cptSearchResults.map((item) => ({
														label: `${item.cpt_code}: ${item.cpt_title}`,
														value: item.cpt_code,
													}))
												: []
										}
										onSelect={(value, option) => {
											handleCodingToolsCodeClick('CPT', {
												code: option.value,
												description: option.label,
												codeTypeText: 'recommendedProcedure',
												buttontext: 'Add in Denial',
											});
										}}
										size="large"
										placeholder="Search CPT Code"
									/>
								</XTheme2Form.Item>
							</XTheme2Form>
						)}
					</Col>
				</Row>

				{/* Related Codes */}
				{activeTab === '1' && (
					<>
						{/* Show related codes when typing */}
						{searchResults && searchResults.length > 0 && !icdCodes.list && (
							<div className="mt-3">
								<h6 className="mb-2">Related Codes</h6>
								{searchResults.map((icdCode, index) => (
									<Card key={index} className="mb-2" size="small">
										<Row gutter={[15, 15]} className="align-items-center">
											<Col xs={20}>
												<div className="fw-bold">{icdCode.icd_code}</div>
												<div className="text-muted small">{icdCode.icd_title}</div>
											</Col>
											<Col xs={4} className="text-end">
												<XButtonConfirm
													className="btnAdd"
													shape="circle"
													icon={<PlusOutlined />}
													type="text"
													size="small"
													onClick={() => {
														handleCodingToolsCodeClick('ICD-10', {
															code: icdCode.icd_code,
															description: icdCode.icd_title,
															codeTypeText: 'pastHistory',
															buttontext: 'Add in Past',
														});
													}}
												/>
											</Col>
										</Row>
									</Card>
								))}
							</div>
						)}

						{/* Show full results when Enter is pressed or after selection */}
						{icdCodes && icdCodes.list && icdCodes.list.length > 0 && (
							<div className="mt-3">
								<h6 className="mb-2">ICD-10 Codes</h6>
								{icdCodes.list.map((icdCode, index) => (
									<Card key={index} className="mb-2" size="small">
										<Row gutter={[15, 15]} className="align-items-center">
											<Col xs={20}>
												<div className="fw-bold">{icdCode.icd_code}</div>
												<div className="text-muted small">{icdCode.icd_title}</div>
											</Col>
											<Col xs={4} className="text-end">
												<XButtonConfirm
													className="btnAdd"
													shape="circle"
													icon={<PlusOutlined />}
													type="text"
													size="small"
													onClick={() => {
														handleCodingToolsCodeClick('ICD-10', {
															code: icdCode.icd_code,
															description: icdCode.icd_title,
															codeTypeText: 'pastHistory',
															buttontext: 'Add in Past',
														});
													}}
												/>
											</Col>
										</Row>
									</Card>
								))}
								{icdCodes.loaded_count < icdCodes.total_count && (
									<Row>
										<Col xs={24} className="text-center">
											<XViewMoreBTN
												loading={icdLoading}
												size="large"
												type="primary"
												onClick={() => {
													handleLoadMore(icdCodes.loaded_count, 'ICD');
												}}
											>
												View More
											</XViewMoreBTN>
										</Col>
									</Row>
								)}
							</div>
						)}
					</>
				)}

				{activeTab === '2' && (
					<>
						{/* Show search results when typing */}
						{cptSearchResults && cptSearchResults.length > 0 && !cptCodes.list && (
							<div className="mt-3">
								<h6 className="mb-2">Search Results</h6>
								{cptSearchResults.map((cptCode, index) => (
									<Card key={index} className="mb-2" size="small">
										<Row gutter={[15, 15]} className="align-items-center">
											<Col xs={20}>
												<div className="fw-bold">{cptCode.cpt_code}</div>
												<div className="text-muted small">{cptCode.cpt_title}</div>
												{cptCode.long_description && (
													<div className="text-muted small mt-1">
														{cptCode.long_description.length > 100
															? `${cptCode.long_description.substring(0, 100)}...`
															: cptCode.long_description}
													</div>
												)}
											</Col>
											<Col xs={4} className="text-end">
												<XButtonConfirm
													className="btnAdd"
													shape="circle"
													icon={<PlusOutlined />}
													type="text"
													size="small"
													onClick={() => {
														handleCodingToolsCodeClick('CPT', {
															code: cptCode.cpt_code,
															description: cptCode.cpt_title,
															codeTypeText: 'recommendedProcedure',
															buttontext: 'Add in Denial',
														});
													}}
												/>
											</Col>
										</Row>
									</Card>
								))}
							</div>
						)}

						{/* Show full results when Enter is pressed or after selection */}
						{cptCodes && cptCodes.list && cptCodes.list.length > 0 && (
							<div className="mt-3">
								<h6 className="mb-2">CPT Codes</h6>
								{cptCodes.list.map((cptCode, index) => (
									<Card key={index} className="mb-2" size="small">
										<Row gutter={[15, 15]} className="align-items-center">
											<Col xs={20}>
												<div className="fw-bold">{cptCode.cpt_code}</div>
												<div className="text-muted small">{cptCode.cpt_title}</div>
												{cptCode.long_description && (
													<div className="text-muted small mt-1">
														{cptCode.long_description.length > 100
															? `${cptCode.long_description.substring(0, 100)}...`
															: cptCode.long_description}
													</div>
												)}
											</Col>
											<Col xs={4} className="text-end">
												<XButtonConfirm
													className="btnAdd"
													shape="circle"
													icon={<PlusOutlined />}
													type="text"
													size="small"
													onClick={() => {
														handleCodingToolsCodeClick('CPT', {
															code: cptCode.cpt_code,
															description: cptCode.cpt_title,
															codeTypeText: 'recommendedProcedure',
															buttontext: 'Add in Denial',
														});
													}}
												/>
											</Col>
										</Row>
									</Card>
								))}
								{cptCodes.loaded_count < cptCodes.total_count && (
									<Row>
										<Col xs={24} className="text-center">
											<XViewMoreBTN
												loading={cptLoading}
												size="large"
												type="primary"
												onClick={() => {
													handleLoadMore(cptCodes.loaded_count, 'CPT');
												}}
											>
												View More
											</XViewMoreBTN>
										</Col>
									</Row>
								)}
							</div>
						)}
					</>
				)}

				{/* Loading State */}
				{(icdLoading || cptLoading || icdSearchLoading || cptSearchLoading) && (
					<div className="text-center mt-3">
						<Spin size="large" />
					</div>
				)}

				{/* No Results */}
				{activeTab === '1' && !icdLoading && !icdSearchLoading && 
					(!searchResults || searchResults.length === 0) && 
					(!icdCodes.list || icdCodes.list.length === 0) && (
					<div className="text-center mt-3 text-muted">
						No ICD-10 codes found
					</div>
				)}

				{activeTab === '2' && !cptLoading && !cptSearchLoading && 
					(!cptSearchResults || cptSearchResults.length === 0) && 
					(!cptCodes.list || cptCodes.list.length === 0) && (
					<div className="text-center mt-3 text-muted">
						No CPT codes found
					</div>
				)}
			</div>
		</XThemeCardCaseOperations>
	);
};

// Redux connection
const mapStateToProps = (state) => ({
	getIcdCodesState: state.getIcdCodes,
	getCptCodesState: state.getCptCodes,
});

const mapDispatchToProps = (dispatch) => ({
	getIcdCodes: (data) => dispatch(getIcdCodesApi(data)),
	getIcdCodesReset: () => dispatch(getIcdCodesReset()),
	getCptCodes: (data) => dispatch(getCptCodesApi(data)),
	getCptCodesReset: () => dispatch(getCptCodesReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CardCodingTools);
